/**
 * Contratos de `/indicadores` e `/conversoes`
 * (`03-endpoints-draft.md §9`, TELA-01, TELA-16).
 *
 * Duas obrigações do contrato que o payload carrega explicitamente:
 *
 *  1. **`meta.atualizadoEm` é obrigatório** (RNF-80): o usuário precisa saber que
 *     o número tem (ou não tem) defasagem. Vem no envelope `EnvelopeAgregado`.
 *  2. **Cada bloco devolve o próprio drill-down** (§9, última linha): um conjunto
 *     de filtros pronto para `GET /leads`, para o clique no card levar à lista
 *     exata que gerou o número. Sem isso, a tela remonta o filtro por conta
 *     própria e as duas contas divergem no primeiro ajuste de regra.
 */
import { z } from 'zod'

import { zDataFiltro, zIdCatalogo, zUuid } from '../comum/campos'
import type { ReferenciaCatalogo } from '../leads/contratos'
import type { StatusLead } from '../leads/enums'
import { VOLUME_MINIMO } from '../meta-ads/metricas'

export const schemaFiltrosIndicadores = z.object({
  de: zDataFiltro.optional(),
  ate: zDataFiltro.optional(),
  unidadeId: zIdCatalogo.optional(),
  equipeId: zIdCatalogo.optional(),
  responsavelUuid: zUuid.optional(),
  pontoConversaoId: zIdCatalogo.optional(),
})

export type FiltrosIndicadores = z.infer<typeof schemaFiltrosIndicadores>

/** Dimensão de agrupamento dos relatórios de conversão e de perdas. */
export const DIMENSOES_AGRUPAMENTO = [
  'pontoConversao',
  'equipe',
  'unidade',
  'responsavel',
  'motivo',
] as const

export type DimensaoAgrupamento = (typeof DIMENSOES_AGRUPAMENTO)[number]

export const schemaFiltrosConversao = schemaFiltrosIndicadores.extend({
  agruparPor: z.enum(DIMENSOES_AGRUPAMENTO).default('pontoConversao'),
})

export type FiltrosConversao = z.infer<typeof schemaFiltrosConversao>

// ---------------------------------------------------------------------------
// Blocos do dashboard — TELA-01
// ---------------------------------------------------------------------------

/** Filtros de `GET /leads` que reproduzem exatamente o número do card. */
export type FiltrosDrilldown = Readonly<Record<string, string>>

export interface FatiaIndicador {
  readonly rotulo: string
  /** `null` quando a fatia é "sem valor" (sem equipe, sem ponto de conversão). */
  readonly id: number | null
  readonly total: number
  /** Percentual sobre o total do bloco, com uma casa (`38.1`). */
  readonly percentual: number
}

export interface BlocoContagem {
  readonly total: number
  readonly porPontoConversao: ReadonlyArray<FatiaIndicador>
  readonly porUnidade: ReadonlyArray<FatiaIndicador>
  readonly drilldown: FiltrosDrilldown
}

/** Faixas do bloco "Aguardando" (`vw_sla_primeiro_atendimento`, ⚠️ Q2). */
export const FAIXAS_ESPERA = ['ATE_30_MIN', 'ATE_2_H', 'ATE_6_H', 'MAIS_DE_1_DIA'] as const

export type FaixaEspera = (typeof FAIXAS_ESPERA)[number]

export const ROTULO_FAIXA_ESPERA: Readonly<Record<FaixaEspera, string>> = {
  ATE_30_MIN: '30 minutos',
  ATE_2_H: '2 horas',
  ATE_6_H: '6 horas',
  MAIS_DE_1_DIA: '+1 dia',
}

export interface BlocoAguardando {
  readonly total: number
  readonly faixas: ReadonlyArray<{
    readonly faixa: FaixaEspera
    readonly rotulo: string
    readonly total: number
    readonly drilldown: FiltrosDrilldown
  }>
  readonly drilldown: FiltrosDrilldown
}

export interface BlocoVendas {
  readonly total: number
  readonly valorTotal: string
  readonly porUnidade: ReadonlyArray<FatiaIndicador>
  readonly drilldown: FiltrosDrilldown
}

export interface BlocoConversao {
  /** `vendas / recebidos` no período, com uma casa. */
  readonly taxa: number
  readonly recebidos: number
  readonly vendas: number
  readonly porPontoConversao: ReadonlyArray<
    FatiaIndicador & { readonly recebidos: number; readonly taxa: number }
  >
}

// ---------------------------------------------------------------------------
// Ranking de velocidade de atendimento — TELA-01
// ---------------------------------------------------------------------------

/**
 * Quantos leads atendidos um usuário precisa ter no período para entrar no ranking.
 *
 * **Reusa `VOLUME_MINIMO.leads`**, e não um número novo: é literalmente o mesmo
 * problema que a constante já resolve no Command Center — "abaixo disto, a razão é
 * informativa, nunca julgamento". Sem corte, quem atendeu um único lead em 40
 * segundos fica em primeiro lugar na frente de quem atendeu trinta com média de
 * quatro minutos, e um ranking assim ninguém olha duas vezes.
 *
 * Quem fica abaixo do corte não some em silêncio: o bloco devolve
 * `abaixoDoMinimo` para a tela poder dizer quantos ficaram de fora e por quê.
 */
export const MINIMO_LEADS_ATENDIDOS_RANKING = VOLUME_MINIMO.leads

export interface LinhaRankingVelocidade {
  /** 1-based **dentro da unidade**, já resolvida pelo servidor — a tela não reordena (§9). */
  readonly posicao: number
  readonly usuario: { readonly uuid: string; readonly nome: string; readonly fotoUrl: string | null }
  /** Leads cujo **primeiro atendimento humano** foi feito por este usuário. */
  readonly leadsAtendidos: number
  /** Média de `primeiro atendimento − chegada do lead`, em segundos inteiros. */
  readonly tempoMedioSegundos: number
}

/**
 * Um ranking por **unidade** — que é como o domínio chama o que a operação chama de
 * departamento (Vendas, Aluguel). Não existe entidade "departamento" no sistema:
 * `leads.unidade_id` é obrigatório e já é o eixo que a barra "Por unidade" dos
 * donuts da TELA-01 usa, então o ranking usa o mesmo.
 *
 * O agrupamento é pela unidade **do lead**, não pela do atendente: é o lead que tem
 * departamento por definição de negócio, e o crédito segue quem atendeu. Na prática
 * as duas coincidem, porque escopo e distribuição já mantêm o atendente dentro da
 * própria unidade — mas quando divergirem, quem manda é o lead.
 *
 * A lista traz **todas** as unidades visíveis ao usuário, inclusive as sem nenhum
 * atendimento no período: departamento vazio precisa de aba própria dizendo que está
 * vazio, senão some da tela e parece que não existe.
 */
export interface GrupoRankingVelocidade {
  readonly unidade: ReferenciaCatalogo
  /** Já ordenadas e posicionadas **dentro da unidade**. */
  readonly linhas: ReadonlyArray<LinhaRankingVelocidade>
  /** Usuários desta unidade com ao menos 1 atendimento, mas abaixo do corte. */
  readonly abaixoDoMinimo: number
}

/**
 * **Sem `drilldown`, de propósito.** Todo outro bloco da TELA-01 devolve filtros
 * prontos para `GET /leads` porque o clique precisa levar à lista exata que gerou o
 * número — e é justamente por isso que este não devolve: `GET /leads` filtra por
 * `responsavelUuid` (dono **atual** do lead), e o ranking credita quem fez o
 * **primeiro atendimento**. São conjuntos diferentes sempre que houve transferência.
 * Um drill-down que abrisse outra lista seria pior do que nenhum.
 */
export interface BlocoRankingVelocidade {
  readonly grupos: ReadonlyArray<GrupoRankingVelocidade>
  /** Corte aplicado, para a tela explicar a ausência sem repetir a regra. */
  readonly minimoLeadsAtendidos: number
}

export interface LeadRecente {
  readonly uuid: string
  readonly clienteNome: string
  readonly status: StatusLead
  readonly chegadaEm: string
  readonly pontoConversao: ReferenciaCatalogo | null
  readonly responsavel: { readonly uuid: string; readonly nome: string } | null
  readonly unidade: ReferenciaCatalogo
}

export interface Dashboard {
  readonly periodo: { readonly de: string; readonly ate: string }
  readonly recebidos: BlocoContagem
  readonly aguardando: BlocoAguardando
  readonly emAtendimento: BlocoContagem
  readonly leadsFinalizados: { readonly total: number; readonly drilldown: FiltrosDrilldown }
  readonly agendamentos: { readonly total: number; readonly vencidos: number }
  readonly vendas: BlocoVendas
  readonly conversao: BlocoConversao
  /** "Quem responde mais rapidamente aos leads" — mesmo recorte de "Recebidos". */
  readonly rankingVelocidade: BlocoRankingVelocidade
  readonly leadsRecentes: ReadonlyArray<LeadRecente>
}

// ---------------------------------------------------------------------------
// Funil e perdas — /conversoes
// ---------------------------------------------------------------------------

export interface EtapaFunil {
  readonly etapa:
    | 'RECEBIDOS'
    | 'ATENDIDOS'
    | 'AGENDADOS'
    | 'COM_PROPOSTA'
    | 'VENDA_REALIZADA'
    | 'VENDA_NAO_REALIZADA'
    | 'FINALIZADO'
  readonly rotulo: string
  readonly total: number
  /** Percentual sobre `RECEBIDOS`. */
  readonly percentualDoTopo: number
  readonly drilldown: FiltrosDrilldown
}

export interface Funil {
  readonly periodo: { readonly de: string; readonly ate: string }
  readonly etapas: ReadonlyArray<EtapaFunil>
  readonly taxaConversao: number
  readonly porDimensao: {
    readonly dimensao: DimensaoAgrupamento
    readonly linhas: ReadonlyArray<{
      readonly id: number | null
      readonly rotulo: string
      readonly recebidos: number
      readonly atendidos: number
      readonly vendas: number
      readonly perdas: number
      readonly taxaConversao: number
    }>
  }
}

export interface LinhaMotivoPerda {
  readonly id: number
  readonly rotulo: string
  readonly total: number
  readonly percentual: number
  readonly drilldown: FiltrosDrilldown
}

export interface RelatorioPerdas {
  readonly periodo: { readonly de: string; readonly ate: string }
  readonly total: number
  readonly motivos: ReadonlyArray<LinhaMotivoPerda>
  readonly dimensao: DimensaoAgrupamento
  /** Cruzamento motivo × dimensão escolhida (equipe, ponto de conversão…). */
  readonly cruzamento: ReadonlyArray<{
    readonly id: number | null
    readonly rotulo: string
    readonly total: number
    readonly porMotivo: ReadonlyArray<{ readonly id: number; readonly rotulo: string; readonly total: number }>
  }>
}
